import { useEffect } from "react";
import { useShallow } from "zustand/react/shallow";
import { toRole } from "@/components/users/roles";
import {
  signOutWithJwt,
  subscribeJwtToken,
  useSession,
} from "@/lib/auth-client";
import { readSiteClaims } from "@/lib/site-claims";
import { useAuthStore } from "@/stores/auth-store";

/**
 * Subscribes to better-auth's session and the in-memory JWT, mirroring
 * both into `useAuthStore`. Mount once at the app root (see `AuthSync`);
 * other code should use `useAuth()` to read.
 */
export function useAuthSync() {
  const { data: session, isPending } = useSession();
  const setSession = useAuthStore((s) => s.setSession);
  const setSiteIds = useAuthStore((s) => s.setSiteIds);
  const setIsPending = useAuthStore((s) => s.setIsPending);
  const clear = useAuthStore((s) => s.clear);

  // Mirror loading state into the store so consumers don't need useSession().
  useEffect(() => {
    setIsPending(isPending);
  }, [isPending, setIsPending]);

  // Mirror session content. Reads only the primitive fields we care about
  // so referential churn on the session object doesn't cause spurious
  // updates — the store's setSession also no-ops if nothing changed.
  useEffect(() => {
    if (!session?.user) {
      clear();
      return;
    }
    const sessionUser = session.user as {
      id: string;
      email: string;
      name?: string | null;
      image?: string | null;
      role?: string | null;
    };
    const sessionData = session.session as {
      activeOrganizationId?: string | null;
    };
    setSession({
      user: {
        id: sessionUser.id,
        email: sessionUser.email,
        name: sessionUser.name,
        image: sessionUser.image,
      },
      role: sessionUser.role ? toRole(sessionUser.role) : null,
      activeOrganizationId: sessionData?.activeOrganizationId ?? null,
      siteIds: readSiteClaims(),
    });
  }, [session, setSession, clear]);

  // Refresh siteClaims whenever the JWT rotates (the session object can
  // stay referentially stable while the token is renewed).
  useEffect(() => {
    return subscribeJwtToken(() => {
      setSiteIds(readSiteClaims());
    });
  }, [setSiteIds]);
}

/**
 * Primary auth hook for components — reads only from the zustand store.
 * Prefer this over `useSession()` + `session.user.role === "admin"`.
 *
 *   const { user, role, isPlatformAdmin, canAccess, canView } = useAuth();
 *   if (canAccess("manager", siteId)) { ... }
 */
export function useAuth() {
  const slice = useAuthStore(
    useShallow((s) => ({
      user: s.user,
      role: s.role,
      activeOrganizationId: s.activeOrganizationId,
      siteIds: s.siteIds,
      isPending: s.isPending,
    })),
  );

  return {
    ...slice,
    isAuthenticated: !!slice.user,
    isPlatformAdmin: slice.role === "admin",
    // Bind at call time so checks read fresh store state.
    canAccess: useAuthStore.getState().canAccess,
    canView: useAuthStore.getState().canView,
    getToken: useAuthStore.getState().getToken,
    signOut: signOutWithJwt,
  };
}
